import React, { useState, useRef, useEffect } from "react";
import {
    CheckSquare, Plus, Trash2, MoreHorizontal, FileText, X, Loader2,
    ChevronDown, ChevronUp, Pencil, Check,
} from "lucide-react";
import * as api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";

function ProgressBar({ completed, total }) {
    const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
    return (
        <div className="flex items-center gap-2">
            <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all ${pct === 100 ? "bg-green-500" : "bg-blue-500"}`}
                    style={{ width: `${pct}%` }} />
            </div>
            <span className="text-xs text-gray-500 whitespace-nowrap">{completed}/{total}</span>
        </div>
    );
}

function ChecklistCard({ checklist, onUpdate, isAgentOrAdmin, getUserById }) {
    const [newItemLabel, setNewItemLabel] = useState("");
    const [addingItem, setAddingItem] = useState(false);
    const [showAddInput, setShowAddInput] = useState(false);
    const [editingTitle, setEditingTitle] = useState(false);
    const [titleValue, setTitleValue] = useState(checklist.title);
    const [togglingId, setTogglingId] = useState(null);
    const inputRef = useRef(null);

    useEffect(() => { if (showAddInput && inputRef.current) inputRef.current.focus(); }, [showAddInput]);

    const completed = checklist.items.filter((i) => i.is_completed).length;
    const total = checklist.items.length;

    const handleToggle = async (itemId) => {
        try {
            setTogglingId(itemId);
            await api.toggleChecklistItem(checklist.id, itemId);
            onUpdate();
        } catch (e) { toast.error("Erreur"); }
        finally { setTogglingId(null); }
    };

    const handleAddItem = async (e) => {
        e.preventDefault();
        if (!newItemLabel.trim()) return;
        try {
            setAddingItem(true);
            await api.addChecklistItem(checklist.id, newItemLabel.trim());
            setNewItemLabel("");
            onUpdate();
        } catch { toast.error("Erreur"); }
        finally { setAddingItem(false); }
    };

    const handleDeleteItem = async (itemId) => {
        try {
            await api.deleteChecklistItem(checklist.id, itemId);
            onUpdate();
        } catch { toast.error("Erreur"); }
    };

    const handleDeleteChecklist = async () => {
        if (!window.confirm("Supprimer cette checklist ?")) return;
        try {
            await api.deleteChecklist(checklist.id);
            toast.success("Checklist supprimée");
            onUpdate();
        } catch { toast.error("Erreur"); }
    };

    const handleSaveTitle = async () => {
        if (titleValue.trim() && titleValue !== checklist.title) {
            await api.updateChecklist(checklist.id, { title: titleValue.trim() });
            onUpdate();
        }
        setEditingTitle(false);
    };

    return (
        <div className="border border-gray-200 rounded-lg" data-testid={`checklist-${checklist.id}`}>
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    <CheckSquare className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    {editingTitle ? (
                        <div className="flex items-center gap-1 flex-1">
                            <Input value={titleValue} onChange={(e) => setTitleValue(e.target.value)} className="h-7 text-sm" autoFocus
                                onKeyDown={(e) => { if (e.key === "Enter") handleSaveTitle(); if (e.key === "Escape") setEditingTitle(false); }} />
                            <button onClick={handleSaveTitle} className="p-1 text-green-600 hover:bg-green-50 rounded"><Check className="w-3.5 h-3.5" /></button>
                        </div>
                    ) : (
                        <span className="text-sm font-semibold text-gray-900 truncate cursor-pointer" onClick={() => isAgentOrAdmin && setEditingTitle(true)}>{checklist.title}</span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <ProgressBar completed={completed} total={total} />
                    {isAgentOrAdmin && (
                        <button onClick={handleDeleteChecklist} className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" data-testid={`delete-checklist-${checklist.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>
            </div>
            <div className="p-2">
                {checklist.items.map((item) => {
                    const completedBy = item.completed_by ? getUserById(item.completed_by) : null;
                    return (
                        <div key={item.id} className="flex items-start gap-2 px-2 py-1.5 rounded hover:bg-gray-50 group" data-testid={`checklist-item-${item.id}`}>
                            {isAgentOrAdmin ? (
                                <button onClick={() => handleToggle(item.id)} disabled={togglingId === item.id}
                                    className={`mt-0.5 w-4 h-4 flex-shrink-0 border rounded transition-colors ${item.is_completed ? "bg-blue-600 border-blue-600 text-white" : "border-gray-300 hover:border-blue-400"}`}
                                    data-testid={`toggle-item-${item.id}`}>
                                    {togglingId === item.id ? <Loader2 className="w-3 h-3 animate-spin" /> : item.is_completed ? <Check className="w-3 h-3" /> : null}
                                </button>
                            ) : (
                                <div className={`mt-0.5 w-4 h-4 flex-shrink-0 border rounded ${item.is_completed ? "bg-blue-600 border-blue-600 text-white flex items-center justify-center" : "border-gray-300"}`}>
                                    {item.is_completed && <Check className="w-3 h-3" />}
                                </div>
                            )}
                            <div className="flex-1 min-w-0">
                                <span className={`text-sm ${item.is_completed ? "line-through text-gray-400" : "text-gray-900"}`}>{item.label}</span>
                                {item.is_completed && completedBy && (
                                    <div className="text-[10px] text-gray-400 mt-0.5">
                                        par {completedBy.first_name} {completedBy.last_name} le {new Date(item.completed_at).toLocaleDateString("fr-FR")}
                                    </div>
                                )}
                            </div>
                            {isAgentOrAdmin && (
                                <button onClick={() => handleDeleteItem(item.id)} className="p-0.5 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <X className="w-3 h-3" />
                                </button>
                            )}
                        </div>
                    );
                })}
                {/* Add item inline */}
                {isAgentOrAdmin && (
                    showAddInput ? (
                        <form onSubmit={handleAddItem} className="flex items-center gap-1 px-2 py-1">
                            <Input ref={inputRef} value={newItemLabel} onChange={(e) => setNewItemLabel(e.target.value)} placeholder="Nouvel item..." className="h-7 text-sm flex-1" disabled={addingItem} data-testid="add-item-input" />
                            <button type="submit" disabled={addingItem} className="p-1 text-blue-600 hover:bg-blue-50 rounded"><Check className="w-3.5 h-3.5" /></button>
                            <button type="button" onClick={() => { setShowAddInput(false); setNewItemLabel(""); }} className="p-1 text-gray-400 hover:bg-gray-100 rounded"><X className="w-3.5 h-3.5" /></button>
                        </form>
                    ) : (
                        <button onClick={() => setShowAddInput(true)} className="flex items-center gap-1 px-2 py-1.5 text-xs text-gray-400 hover:text-blue-600 transition-colors w-full" data-testid={`add-item-btn-${checklist.id}`}>
                            <Plus className="w-3 h-3" /> Ajouter un item
                        </button>
                    )
                )}
            </div>
        </div>
    );
}

export function TicketChecklists({ ticket, checklists, onUpdate, currentUser, isAgentOrAdmin, getUserById }) {
    const [showNewMenu, setShowNewMenu] = useState(false);
    const [newTitle, setNewTitle] = useState("");
    const [templates, setTemplates] = useState([]);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [creating, setCreating] = useState(false);
    const [newTab, setNewTab] = useState("empty");
    const menuRef = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setShowNewMenu(false); };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const openNewMenu = async () => {
        setShowNewMenu(true);
        setNewTab("empty");
        setNewTitle("");
        if (!loadingTemplates && templates.length === 0) {
            setLoadingTemplates(true);
            try {
                const data = await api.getChecklistTemplates(ticket.type);
                setTemplates(data);
            } catch (e) { console.error("Failed to load checklist templates:", e); }
            finally { setLoadingTemplates(false); }
        }
    };

    const handleCreateEmpty = async () => {
        if (!newTitle.trim()) { toast.error("Le titre est requis"); return; }
        try {
            setCreating(true);
            await api.createChecklist(ticket.id, { title: newTitle.trim(), items: [] });
            toast.success("Checklist créée");
            setShowNewMenu(false);
            onUpdate();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
        finally { setCreating(false); }
    };

    const handleCreateFromTemplate = async (templateId) => {
        try {
            setCreating(true);
            await api.createChecklistFromTemplate(ticket.id, templateId);
            toast.success("Checklist créée depuis le template");
            setShowNewMenu(false);
            onUpdate();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
        finally { setCreating(false); }
    };

    if (!checklists || checklists.length === 0) {
        if (!isAgentOrAdmin) return null;
        return (
            <div className="relative" ref={menuRef}>
                <button onClick={openNewMenu} className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-blue-600 transition-colors" data-testid="add-checklist-btn">
                    <Plus className="w-4 h-4" /> Ajouter une checklist
                </button>
                {showNewMenu && <NewChecklistMenu newTab={newTab} setNewTab={setNewTab} newTitle={newTitle} setNewTitle={setNewTitle}
                    templates={templates} loadingTemplates={loadingTemplates} creating={creating}
                    onCreateEmpty={handleCreateEmpty} onCreateFromTemplate={handleCreateFromTemplate} />}
            </div>
        );
    }

    return (
        <div className="space-y-3" data-testid="ticket-checklists">
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <CheckSquare className="w-4 h-4" /> Checklists
                </h3>
                {isAgentOrAdmin && (
                    <div className="relative" ref={menuRef}>
                        <button onClick={openNewMenu} className="flex items-center gap-1 text-xs text-gray-400 hover:text-blue-600" data-testid="add-checklist-btn">
                            <Plus className="w-3.5 h-3.5" /> Nouvelle
                        </button>
                        {showNewMenu && <NewChecklistMenu newTab={newTab} setNewTab={setNewTab} newTitle={newTitle} setNewTitle={setNewTitle}
                            templates={templates} loadingTemplates={loadingTemplates} creating={creating}
                            onCreateEmpty={handleCreateEmpty} onCreateFromTemplate={handleCreateFromTemplate} />}
                    </div>
                )}
            </div>
            {checklists.map((cl) => (
                <ChecklistCard key={cl.id} checklist={cl} onUpdate={onUpdate} isAgentOrAdmin={isAgentOrAdmin} getUserById={getUserById} />
            ))}
        </div>
    );
}

function NewChecklistMenu({ newTab, setNewTab, newTitle, setNewTitle, templates, loadingTemplates, creating, onCreateEmpty, onCreateFromTemplate }) {
    return (
        <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-xl w-72" data-testid="new-checklist-menu">
            <div className="flex border-b border-gray-100">
                <button onClick={() => setNewTab("empty")} className={`flex-1 px-3 py-2 text-xs font-medium ${newTab === "empty" ? "text-blue-600 border-b-2 border-blue-600" : "text-gray-500"}`}>Vide</button>
                <button onClick={() => setNewTab("template")} className={`flex-1 px-3 py-2 text-xs font-medium ${newTab === "template" ? "text-blue-600 border-b-2 border-blue-600" : "text-gray-500"}`}>Depuis un template</button>
            </div>
            <div className="p-3">
                {newTab === "empty" ? (
                    <div className="space-y-2">
                        <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="Titre de la checklist" className="h-8 text-sm" autoFocus
                            onKeyDown={(e) => { if (e.key === "Enter") onCreateEmpty(); }} data-testid="checklist-title-input" />
                        <Button onClick={onCreateEmpty} disabled={creating} size="sm" className="w-full" data-testid="create-empty-checklist-btn">
                            {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Plus className="w-3.5 h-3.5 mr-1" />} Créer
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-1 max-h-48 overflow-y-auto">
                        {loadingTemplates ? <div className="text-center py-4 text-sm text-gray-400"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div> :
                         templates.length === 0 ? <div className="text-center py-4 text-xs text-gray-400">Aucun template disponible pour ce type de ticket</div> :
                         templates.map((t) => (
                            <button key={t.id} onClick={() => onCreateFromTemplate(t.id)} disabled={creating}
                                className="w-full text-left px-3 py-2 text-sm rounded hover:bg-blue-50 transition-colors flex items-center justify-between"
                                data-testid={`template-${t.id}`}>
                                <span className="font-medium text-gray-900">{t.name}</span>
                                <span className="text-xs text-gray-400">{t.items?.length || 0} items</span>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
